import React, { Fragment, useCallback, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { SupButton, SupCheckbox, SupText } from "@repo/core-ui";

import { useStoredData, useStoredDataDispatch } from "@/store/Context";
import { SupStoredRewardTokenValuations } from "@/store/types";
import LayoutSidebarAtomsRewardsTokenValuation from "@/components/Layout/Sidebar/Atoms/RewardsTokenValuation";
import useVaults from "@/hooks/api/useVaults";

interface LayoutSidebarModeRewardsApyProps {
  className?: string;
  headerClassName?: string;
}

export default function LayoutSidebarModeRewardsApy({ className, headerClassName }: LayoutSidebarModeRewardsApyProps) {
  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();

  const includeRewardsInApy = useMemo(() => storedData.includeRewardsInApy, [storedData]);

  const { data: rawVaults } = useVaults();

  const customizableRewardTokens = useMemo(() => {
    const customizableRewardTokens = [] as SupStoredRewardTokenValuations[];
    rawVaults
      ?.filter(vault => (vault.rewards?.length ?? 0) > 0)
      .forEach(vault => {
        vault.rewards?.forEach(reward => {
          if (reward.type === "token" && reward.symbol !== "" && reward.is_configurable && !customizableRewardTokens.some(token => token.id === reward.address)) {
            const storedTokenData = storedData.rewardTokenValuations.find(token => token.id === reward.address);

            customizableRewardTokens.push({
              id: reward.address,
              token_address: reward.address,
              token_symbol: reward.symbol,
              chain_id: reward.chain_id,
              price: 1,
              custom_price: storedTokenData?.custom_price ?? 1,
              token_image: reward.logo,
              total_supply: reward.total_supply ?? 0,
            });
          }
        });
      });

    return customizableRewardTokens;
  }, [rawVaults, storedData.rewardTokenValuations]);

  const [customTokensValuation, setCustomTokensValuation] = useState<SupStoredRewardTokenValuations[]>([]);

  const handleIncludeRewardsToggle = useCallback(() => {
    storedDispatch({ type: "toggleIncludeRewardsInApy" });
  }, [storedDispatch]);

  const handleCustomTokenValuationChange = useCallback(
    (token: SupStoredRewardTokenValuations) => {
      setCustomTokensValuation(customTokensValuation.filter(t => t.id !== token.id).concat(token));
    },
    [customTokensValuation]
  );

  const isApplyDisabled = useMemo(() => customTokensValuation.length < 1, [customTokensValuation.length]);

  const handleApplyCustomTokensValuation = useCallback(() => {
    storedDispatch({ type: "setRewardTokensValuation", rewardTokenValuations: customTokensValuation });
    setCustomTokensValuation([]);
    storedDispatch({ type: "closeOverlay" });
  }, [customTokensValuation, storedDispatch]);

  return (
    <section className={twMerge("relative flex h-full max-h-screen flex-col overflow-y-scroll p-6 pt-[60px]", className)}>
      <header className={twMerge("fixed top-0 z-50 flex w-full items-center justify-start space-x-2 py-2.5", headerClassName)}>
        <SupText variant="subtitle1" weight="bold" className="leading-10">
          Apy settings
        </SupText>
      </header>
      <div className="h-20 space-y-4">
        <SupText variant="caption" weight="bold" className="text-neutral-500">
          Settings
        </SupText>
        <div className="border-[0.5px] border-neutral-200 bg-neutral-25 p-4">
          <div className="flex h-5 items-center space-x-2">
            <SupText variant="caption">Include rewards in APY</SupText>
            <SupCheckbox checked={includeRewardsInApy} onChange={handleIncludeRewardsToggle} />
          </div>
        </div>
      </div>
      {customizableRewardTokens.length > 0 && (
        <Fragment>
          <SupText variant="caption" weight="bold" className="my-4 text-neutral-500">
            Expected valuations
          </SupText>
          <div className="scrollbar-custom flex h-full w-full flex-grow flex-col space-y-4 overflow-y-auto overflow-x-hidden pb-1 pt-0">
            {customizableRewardTokens.map(token => (
              <LayoutSidebarAtomsRewardsTokenValuation onChange={handleCustomTokenValuationChange} token={token} key={`rewards-token-valuation-${token.id}`} />
            ))}
          </div>
          <footer className="pt-6">
            <SupButton disabled={isApplyDisabled} fluid onClick={handleApplyCustomTokensValuation}>
              Apply
            </SupButton>
          </footer>
        </Fragment>
      )}
    </section>
  );
}
